import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_BASE } from "../../api.js";
import logoImg from "../Images/logo40.jpg";

const FIELDS = [
  { name: "name",      label: "signup.name",      type: "text",     required: true },
  { name: "specialty", label: "signup.specialty",  type: "text",     required: true },
  { name: "email",     label: "signup.email",      type: "email",    required: true },
  { name: "password",  label: "signup.password",   type: "password", required: true },
  { name: "country",   label: "signup.country",    type: "text",     required: true },
  { name: "address",   label: "signup.address",    type: "text",     required: true },
  { name: "telephone", label: "signup.telephone",  type: "tel",      required: false },
  { name: "website",   label: "signup.website",    type: "url",      required: false },
  { name: "instagram", label: "signup.instagram",  type: "text",     required: false },
  { name: "facebook",  label: "signup.facebook",   type: "text",     required: false },
];

function validate(values, t) {
  const errors = {};
  if (!values.name?.trim())      errors.name      = t("signup.required");
  if (!values.specialty?.trim()) errors.specialty = t("signup.required");
  if (!values.email?.trim())     errors.email     = t("signup.required");
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errors.email = t("signup.emailInvalid");
  if (!values.password)          errors.password  = t("signup.required");
  else if (values.password.length < 8)             errors.password = t("signup.passwordShort");
  if (!values.country?.trim())   errors.country   = t("signup.required");
  if (!values.address?.trim())   errors.address   = t("signup.required");
  if (!values.intro?.trim())     errors.intro     = t("signup.required");
  if (!values.biography?.trim()) errors.biography = t("signup.required");
  if (!values.profileImage)      errors.profileImage = t("signup.required");
  return errors;
}

function IncompleteModal({ onClose, onEdit, t }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:"var(--bg,#fff)", borderRadius:12, padding:"32px 28px", maxWidth:480, width:"100%", boxShadow:"0 8px 40px rgba(0,0,0,0.3)" }}>
        <h3 style={{ marginTop:0, color:"#bf360c" }}>{t("signup.incompleteTitle") || "Profile incomplete"}</h3>
        <p>{t("signup.incompleteBody") || "Your account has been created, but your artist card won't appear on the Artists page until you upload at least one artwork image."}</p>
        <div style={{ direction:"rtl", textAlign:"right", borderTop:"1px solid #e0d8d0", paddingTop:16, marginTop:16 }}>
          <p style={{ color:"#555" }}>{t("signup.incompleteBodyAr") || "تم إنشاء حسابك، لكن بطاقتك الفنية لن تظهر في صفحة الفنانين حتى ترفع صورة فنية واحدة على الأقل."}</p>
        </div>
        <div style={{ display:"flex", gap:10, marginTop:20 }}>
          <button onClick={onEdit} style={{ flex:1, padding:"10px 16px", background:"#bf360c", color:"#fff", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600 }}>
            {t("signup.completeNow") || "Complete Profile"}
          </button>
          <button onClick={onClose} style={{ flex:1, padding:"10px 16px", background:"transparent", border:"1px solid #ccc", borderRadius:6, cursor:"pointer" }}>
            {t("signup.completeLater") || "Later"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SignUp() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [values, setValues]           = useState({});
  const [artworkFiles, setArtworkFiles] = useState([null, null, null, null]);
  const [errors, setErrors]           = useState({});
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting]   = useState(false);
  const [showModal, setShowModal]     = useState(false);
  const [createdId, setCreatedId]     = useState(null);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) setValues((v) => ({ ...v, [name]: files[0] }));
    else       setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const handleArtwork = (index, file) => {
    setArtworkFiles((prev) => { const next = [...prev]; next[index] = file; return next; });
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setServerError("");
    const errs = validate(values, t);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      document.querySelector(".spar-input-error")?.scrollIntoView({ behavior:"smooth", block:"center" });
      return;
    }
    const formData = new FormData();
    Object.entries(values).forEach(([key, val]) => {
      if (key !== "profileImage" && val !== undefined && val !== "") formData.append(key, val);
    });
    if (values.profileImage) formData.append("profileImage", values.profileImage);
    artworkFiles.forEach((f) => { if (f) formData.append("image", f); });

    try {
      setSubmitting(true);
      const res = await axios.post(`${API_BASE}/createRally`, formData);
      setCreatedId(res.data._id);
      const hasArtworks = artworkFiles.some(f => f !== null);
      if (!hasArtworks) {
        setShowModal(true);
      } else {
        navigate(`/ArtistProfile/${res.data._id}`, { state: { justCreated: true } });
      }
    } catch (err) {
      const msg = err.response?.data?.error || t("error.generic");
      if (msg.includes("already exists") || err.response?.status === 409) {
        setErrors((er) => ({ ...er, email: t("signup.emailTaken") }));
      } else {
        setServerError(msg);
      }
      setSubmitting(false);
    }
  };

  const goToProfile = () => {
    if (createdId) navigate(`/ArtistProfile/${createdId}`, { state: { justCreated: true } });
    else navigate("/ArtistsList");
  };

  const goToEdit = () => {
    if (createdId) navigate(`/edit-profile/${createdId}`);
    else navigate("/ArtistsList");
  };

  return (
    <>
      {showModal && (
        <IncompleteModal
          t={t}
          onClose={goToProfile}
          onEdit={goToEdit}
        />
      )}
      <div style={{ maxWidth:680, margin:"100px auto 60px", padding:"0 20px" }}>
        <div className="spar-card" style={{ padding:"40px 36px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:28 }}>
            <img src={logoImg} alt="SPAR" style={{ width:52, height:52, borderRadius:10 }} />
            <div>
              <h1 className="spar-page-title" style={{ marginBottom:2 }}>{t("signup.title")}</h1>
              <p style={{ color:"var(--text-3)", fontSize:"0.875rem", margin:0 }}>{t("signup.subtitle")}</p>
            </div>
          </div>

          <p style={{ background:"var(--accent-light)", border:"1px solid rgba(191,54,12,0.18)", borderRadius:"var(--radius-sm)", padding:"12px 16px", fontSize:"0.875rem", color:"var(--text-secondary)", marginBottom:28, lineHeight:1.6 }}>
            {t("signup.communityText")}
          </p>

          {serverError && <div className="spar-alert spar-alert-error">{serverError}</div>}

          <form onSubmit={handleSubmit} noValidate>
            <div className="spar-form-group">
              <label className="spar-label">{t("signup.profileImage")} *</label>
              <input type="file" accept="image/*" name="profileImage" className={`spar-input${errors.profileImage ? " spar-input-error" : ""}`} onChange={handleChange} />
              {errors.profileImage && <p className="spar-error-msg">{errors.profileImage}</p>}
            </div>

            {FIELDS.map(({ name, label, type, required }) => (
              <div className="spar-form-group" key={name}>
                <label className="spar-label">{t(label)} {required && "*"}</label>
                <input type={type} name={name} className={`spar-input${errors[name] ? " spar-input-error" : ""}`} onChange={handleChange} autoComplete={name === "password" ? "new-password" : "off"} />
                {name === "password" && !errors.password && <p style={{ fontSize:"0.78rem", color:"var(--text-3)", marginTop:4 }}>{t("signup.passwordHint")}</p>}
                {errors[name] && <p className="spar-error-msg">{errors[name]}</p>}
              </div>
            ))}

            <div className="spar-form-group">
              <label className="spar-label">{t("signup.intro")} *</label>
              <textarea name="intro" rows={4} maxLength={500} className={`spar-textarea${errors.intro ? " spar-input-error" : ""}`} onChange={handleChange} />
              <p style={{ fontSize:"0.75rem", color:"var(--text-3)", marginTop:4 }}>{(values.intro || "").length}/500</p>
              {errors.intro && <p className="spar-error-msg">{errors.intro}</p>}
            </div>

            <div className="spar-form-group">
              <label className="spar-label">{t("signup.biography")} *</label>
              <textarea name="biography" rows={7} maxLength={1500} className={`spar-textarea${errors.biography ? " spar-input-error" : ""}`} onChange={handleChange} />
              <p style={{ fontSize:"0.75rem", color:"var(--text-3)", marginTop:4 }}>{(values.biography || "").length}/1500</p>
              {errors.biography && <p className="spar-error-msg">{errors.biography}</p>}
            </div>

            <div className="spar-form-group" style={{ background:"rgba(191,54,12,0.05)", border:"1px solid rgba(191,54,12,0.15)", borderRadius:8, padding:"18px 16px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
                <span style={{ fontSize:"1rem" }}>🌐</span>
                <span style={{ fontWeight:700, fontSize:"0.9rem" }}>{t("signup.arabicFields") || "Arabic Fields"}</span>
                <span style={{ fontSize:"0.78rem", color:"var(--text-3)" }}>{t("signup.arabicOptional") || "(optional — helps Arabic-speaking visitors)"}</span>
              </div>
              <div style={{ marginBottom:12 }}>
                <label className="spar-label" style={{ direction:"rtl", textAlign:"right", display:"block" }}>{t("signup.introAr") || "نبذة قصيرة بالعربية"}</label>
                <textarea name="introAr" rows={3} maxLength={500} className="spar-textarea" dir="rtl" style={{ fontFamily:"Cairo, Tajawal, sans-serif" }} onChange={handleChange} placeholder="اكتب نبذتك القصيرة بالعربية هنا…" />
              </div>
              <div>
                <label className="spar-label" style={{ direction:"rtl", textAlign:"right", display:"block" }}>{t("signup.biographyAr") || "السيرة الذاتية بالعربية"}</label>
                <textarea name="biographyAr" rows={6} maxLength={1500} className="spar-textarea" dir="rtl" style={{ fontFamily:"Cairo, Tajawal, sans-serif" }} onChange={handleChange} placeholder="اكتب سيرتك الذاتية الكاملة بالعربية هنا…" />
              </div>
            </div>

            <div className="spar-form-group">
              <label className="spar-label">{t("signup.artworks")}</label>
              <p style={{ fontSize:"0.82rem", color:"var(--text-3)", marginBottom:10 }}>
                {t("signup.artworksHint") || "Upload at least 1 artwork for your card to appear on the Artists page."}
              </p>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                {artworkFiles.map((_, i) => (
                  <div key={i}>
                    <label className="spar-label" style={{ fontSize:"0.78rem" }}>{t("signup.artwork")} {i + 1}</label>
                    <input type="file" accept="image/*" className="spar-input" onChange={(e) => handleArtwork(i, e.target.files[0])} />
                  </div>
                ))}
              </div>
            </div>

            <button type="submit" className="spar-btn spar-btn-primary" disabled={submitting} style={{ width:"100%", justifyContent:"center", height:48, marginTop:8 }}>
              {submitting ? (<><span className="spar-spinner" />{t("signup.submitting")}</>) : t("signup.submit")}
            </button>

            <p style={{ textAlign:"center", marginTop:18, fontSize:"0.875rem", color:"var(--text-3)" }}>
              {t("signup.haveAccount")}{" "}
              <a href="/login" style={{ fontWeight:700 }}>{t("signup.loginLink")}</a>
            </p>
          </form>
        </div>
      </div>
    </>
  );
}
